// PriceScraperV2.js — EC2-backed DB-queued price scraper tab.
//
// Differences from the "Quick Scrape" tab:
//   * A submitted job is persisted in `price_scraper_jobs` and processed by
//     the long-running `safavieh-scraper.service` on EC2.
//   * Any user who opens this tab sees the same shared job list, live progress,
//     and results — state is server-owned, not tied to any one browser.
//   * Survives tab close, browser close, deploys, and EB app-pool recycles.
//   * Browser-push notification when the job completes (optional opt-in).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel,
  IconButton, InputAdornment, LinearProgress, Link, Pagination, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Stack, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Divider,
} from '@mui/material';
import {
  CloudUpload, Stop, Refresh, PlayArrow, Clear, Notifications, NotificationsOff,
  Search, GetApp, Storefront,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS } from '../config/api';
import { TOOL_COLORS } from '../constants/colors';
import { usePermissions } from '../context/PermissionContext';

// Match the "Other Tools" category palette (dark orange).
const ACCENT = TOOL_COLORS.OTHER.primary;
const ACCENT_HOVER = TOOL_COLORS.OTHER.hover;
const ACCENT_LIGHT = TOOL_COLORS.OTHER.light;

// The BBB catalog is built; BBB now resolves via the catalog during a normal
// scrape (size/color/shape match), so the standalone catalog panel is hidden.
const SHOW_BBB_PANEL = false;

const SHOW_QVC_CATALOG = false;

// The Raymour / Overstock / Belk catalogs are built; their "Build / Refresh
// Catalog" panels are hidden from the page (Klevi, 2026-10-20). Endpoints,
// workers and state hooks are untouched — flip this back to true to show them.
const SHOW_CATALOG_PANELS = false;

const SITES = [
  { key: 'amazon',    label: 'Amazon' },
  { key: 'walmart',   label: 'Walmart' },
  { key: 'lowes',     label: "Lowe's" },
  { key: 'bbb',       label: 'Bed Bath & Beyond' },
  { key: 'homedepot', label: 'Home Depot' },
  { key: 'target',    label: 'Target' },
  { key: 'kohls',     label: "Kohl's" },
  { key: 'nfm',       label: 'Nebraska Furniture Mart' },
  { key: 'ashley',    label: 'Ashley Furniture' },
  { key: 'qvc',       label: 'QVC' },
  { key: 'chairish',  label: 'Chairish' },
  { key: 'antiquefarmhouse', label: 'Antique Farmhouse' },
  { key: 'menards',   label: 'Menards' },
  { key: 'belk',      label: 'Belk' },
  { key: 'raymour',   label: 'Raymour & Flanigan' },
  // Overstock relaunched as its own storefront (separate assortment + prices
  // from BBB). Resolved through the Overstock catalog built below, like BBB.
  { key: 'overstock', label: 'Overstock' },
];

// ─── Small helpers ─────────────────────────────────────────────────────────
const fmtDuration = (start, end) => {
  if (!start) return '—';
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  let sec = Math.max(0, Math.floor((e - s) / 1000));
  const h = Math.floor(sec / 3600); sec -= h * 3600;
  const m = Math.floor(sec / 60); sec -= m * 60;
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const statusChipColor = (status) => ({
  preparing:  'info',
  queued:     'default',
  processing: 'warning',
  completed:  'success',
  cancelled:  'default',
  failed:     'error',
}[status] || 'default');

const authedFetch = async (url, options = {}) => {
  const token = localStorage.getItem('jwtToken');
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); if (j?.message) msg = j.message; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
};

// Persist the "currently watched job id" in localStorage so refreshing the
// browser drops you right back on the job you were watching.
const WATCH_KEY = 'priceScraperV2_watchJobId';
const NOTIFY_KEY = 'priceScraperV2_notifyEnabled';

export default function PriceScraperV2() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { isAdmin, isOwner } = usePermissions();
  const canBuildCatalog = isAdmin || isOwner;

  // BBB catalog crawl state
  const [bbbStatus, setBbbStatus]       = useState(null);   // { catalogTotal, catalogWithItemId, job }
  const [bbbConfirmOpen, setBbbConfirm] = useState(false);
  const [bbbBusy, setBbbBusy]           = useState(false);
  const [bbbLookupId, setBbbLookupId]   = useState('');
  const [bbbLookup, setBbbLookup]       = useState(null);   // { found, item } | { error }
  const [bbbMsg, setBbbMsg]             = useState(null);

  // QVC catalog crawl state (own panel — QVC needs a code -> URL catalog
  // because their search doesn't work on our CustomerItemCode)
  const [qvcStatus, setQvcStatus]       = useState(null);   // { catalogTotal, catalogProducts, job }
  const [qvcConfirmOpen, setQvcConfirm] = useState(false);
  const [belkStatus, setBelkStatus] = useState(null);
  const [belkBusy, setBelkBusy] = useState(false);
  const [belkMsg, setBelkMsg] = useState(null);
  const [belkConfirmOpen, setBelkConfirm] = useState(false);
  const [rayStatus, setRayStatus] = useState(null);
  const [rayBusy, setRayBusy] = useState(false);
  const [rayMsg, setRayMsg] = useState(null);
  const [ovrStatus, setOvrStatus] = useState(null);   // { catalogTotal, catalogWithItemId, job }
  const [bpStatus, setBpStatus] = useState(null);     // Beyond partner map: { sites, job, canUpload }
  const [bpBusy, setBpBusy] = useState(false);
  const [bpMsg, setBpMsg] = useState(null);
  const bpFileRef = useRef(null);
  const [ovrBusy, setOvrBusy] = useState(false);
  const [ovrMsg, setOvrMsg] = useState(null);
  const [qvcBusy, setQvcBusy]           = useState(false);
  const [qvcMsg, setQvcMsg]             = useState(null);

  // Submit form state
  const [skus, setSkus]                 = useState([]);
  const [parsedSizes, setParsedSizes]   = useState({});
  const [fileName, setFileName]         = useState('');
  const [jobName, setJobName]           = useState('');
  const [sitesSelected, setSitesSelected] = useState({ amazon: true, walmart: true, lowes: true });
  const [submitting, setSubmitting]     = useState(false);
  const [parseError, setParseError]     = useState(null);

  // Job list + watched job
  const [jobs, setJobs]                 = useState([]);
  const [watchedJob, setWatchedJob]     = useState(null);     // full detail of the one we're watching
  const [watchedJobId, setWatchedJobId] = useState(() => {
    const v = localStorage.getItem(WATCH_KEY);
    return v ? parseInt(v, 10) : null;
  });
  const [loadingList, setLoadingList]   = useState(false);
  const [loadError, setLoadError]       = useState(null);

  // Notification opt-in
  const [notifyEnabled, setNotifyEnabled] = useState(() =>
    localStorage.getItem(NOTIFY_KEY) === '1' && typeof Notification !== 'undefined' && Notification.permission === 'granted'
  );

  const fileInputRef = useRef(null);
  const previouslyCompletedIds = useRef(new Set());  // for notification dedup

  // Results table: search + pagination so 5k-row jobs stay smooth.
  // Rendering 5000 rows at once in MUI Table is the main cause of the lag —
  // pagination caps the DOM at PAGE_SIZE rows; search lets the user jump
  // straight to a SKU instead of scrolling.
  const PAGE_SIZE = 100;
  const [skuSearch, setSkuSearch] = useState('');
  const [resultsPage, setResultsPage] = useState(1);

  // ─── Raymour catalog ─────────────────────────────────────────────────────
  const loadRayStatus = useCallback(async () => {
    try {
      setRayStatus(await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_RAYMOUR_CATALOG_STATUS));
    } catch (err) {
      console.error('Raymour catalog status failed', err);
    }
  }, []);

  const startRayBuild = useCallback(async () => {
    setRayBusy(true); setRayMsg(null);
    try {
      await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_RAYMOUR_CATALOG_START, { method: 'POST' });
      setRayMsg({ severity: 'success', text: 'Raymour catalog build queued — it sweeps their sitemaps, usually a few minutes.' });
      loadRayStatus();
    } catch (err) {
      setRayMsg({ severity: 'error', text: err.message });
    } finally {
      setRayBusy(false);
    }
  }, [loadRayStatus]);

  // ─── Beyond partner dashboard (BBB + Overstock exact item map) ───────────
  const BP_ACTIVE = ['queued', 'running'];
  const loadBpStatus = useCallback(async () => {
    try {
      setBpStatus(await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_BEYOND_PARTNER_STATUS));
    } catch (err) {
      console.error('Beyond partner status failed', err);
    }
  }, []);

  const uploadBpFile = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBpBusy(true); setBpMsg(null);
    try {
      const form = new FormData();
      form.append('file', f);
      const token = localStorage.getItem('jwtToken');
      const res = await fetch(API_ENDPOINTS.PRICE_SCRAPER_V2_BEYOND_PARTNER_UPLOAD, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || `HTTP ${res.status}`);
      setBpMsg({ severity: 'success', text: `Uploaded ${f.name} — loading in the background; this page shows the progress.` });
      loadBpStatus();
    } catch (err) {
      setBpMsg({ severity: 'error', text: err.message });
    } finally {
      setBpBusy(false);
      if (bpFileRef.current) bpFileRef.current.value = '';
    }
  }, [loadBpStatus]);

  // ─── Overstock catalog ───────────────────────────────────────────────────
  const OVR_ACTIVE = ['queued', 'listing', 'products'];
  const loadOvrStatus = useCallback(async () => {
    try {
      setOvrStatus(await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_OVERSTOCK_CATALOG_STATUS));
    } catch (err) {
      console.error('Overstock catalog status failed', err);
    }
  }, []);

  const startOvrBuild = useCallback(async () => {
    setOvrBusy(true); setOvrMsg(null);
    try {
      await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_OVERSTOCK_CATALOG_START, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: false }),
      });
      setOvrMsg({ severity: 'success', text: 'Overstock catalog build queued — it sweeps their sitemaps, then fetches every Safavieh product page. Runs on the worker; you can leave this page.' });
      loadOvrStatus();
    } catch (err) {
      setOvrMsg({ severity: 'error', text: err.message });
    } finally {
      setOvrBusy(false);
    }
  }, [loadOvrStatus]);

  const cancelOvrBuild = useCallback(async () => {
    try {
      await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_OVERSTOCK_CATALOG_CANCEL, { method: 'POST' });
      setOvrMsg({ severity: 'info', text: 'Cancel requested — the worker stops after the current batch.' });
      loadOvrStatus();
    } catch (err) {
      setOvrMsg({ severity: 'error', text: err.message });
    }
  }, [loadOvrStatus]);

  // ─── Belk catalog ────────────────────────────────────────────────────────
  const loadBelkStatus = useCallback(async () => {
    try {
      setBelkStatus(await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_BELK_CATALOG_STATUS));
    } catch (err) {
      console.error('Belk catalog status failed', err);
    }
  }, []);

  const startBelkBuild = useCallback(async () => {
    setBelkBusy(true); setBelkMsg(null);
    try {
      await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_BELK_CATALOG_START, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: false }),
      });
      setBelkConfirm(false);
      setBelkMsg({ severity: 'success', text: 'Belk catalog build queued — it runs on the worker; you can leave this page.' });
      loadBelkStatus();
    } catch (err) {
      setBelkMsg({ severity: 'error', text: err.message });
    } finally {
      setBelkBusy(false);
    }
  }, [loadBelkStatus]);

  const cancelBelkBuild = useCallback(async () => {
    try {
      await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_BELK_CATALOG_CANCEL, { method: 'POST' });
      setBelkMsg({ severity: 'info', text: 'Cancellation requested.' });
      loadBelkStatus();
    } catch (err) {
      setBelkMsg({ severity: 'error', text: err.message });
    }
  }, [loadBelkStatus]);

  // ─── QVC catalog ─────────────────────────────────────────────────────────
  const loadQvcStatus = useCallback(async () => {
    try {
      const data = await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_QVC_CATALOG_STATUS);
      setQvcStatus(data);
    } catch (err) {
      // non-fatal: the panel just shows nothing
      console.error('QVC catalog status failed', err);
    }
  }, []);

  // mode 'listing' re-walks QVC's Safavieh brand pages; 'gapfill' skips
  // browsing entirely and fetches just the feed codes we're missing (QVC's
  // product id is the code's base number minus one), which is how the last
  // ~47% gets filled — the brand listing simply doesn't carry those products.
  const startQvcCrawl = useCallback(async (mode = 'listing') => {
    setQvcBusy(true); setQvcMsg(null);
    try {
      await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_QVC_CATALOG_START, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // refresh on a gapfill means "re-attempt products already written off
        // as dead", which is the slow full sweep. Default off: a normal gap
        // fill skips pages that have failed MAX_MISSES passes in a row.
        body: JSON.stringify({ refresh: mode !== 'gapfill', mode }),
      });
      setQvcConfirm(false);
      setQvcMsg({
        severity: 'success',
        text: mode === 'gapfill'
          ? 'Gap fill queued — fetching only the feed codes missing from the catalog. Runs on the worker; you can leave this page.'
          : 'QVC catalog crawl queued — it runs on the worker; you can leave this page.',
      });
      loadQvcStatus();
    } catch (err) {
      setQvcMsg({ severity: 'error', text: err.message });
    } finally {
      setQvcBusy(false);
    }
  }, [loadQvcStatus]);

  const cancelQvcCrawl = useCallback(async () => {
    try {
      await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_QVC_CATALOG_CANCEL, { method: 'POST' });
      setQvcMsg({ severity: 'info', text: 'Cancellation requested.' });
      loadQvcStatus();
    } catch (err) {
      setQvcMsg({ severity: 'error', text: err.message });
    }
  }, [loadQvcStatus]);

  useEffect(() => { loadRayStatus(); }, [loadRayStatus]);
  useEffect(() => {
    const active = rayStatus?.job && ['queued', 'running'].includes(rayStatus.job.status);
    if (!active) return undefined;
    const t = setInterval(loadRayStatus, 5000);
    return () => clearInterval(t);
  }, [rayStatus, loadRayStatus]);

  useEffect(() => { loadBpStatus(); }, [loadBpStatus]);
  useEffect(() => {
    const active = bpStatus?.job && BP_ACTIVE.includes(bpStatus.job.status);
    if (!active) return undefined;
    const t = setInterval(loadBpStatus, 5000);
    return () => clearInterval(t);
  }, [bpStatus, loadBpStatus]);

  useEffect(() => { loadOvrStatus(); }, [loadOvrStatus]);
  useEffect(() => {
    const active = ovrStatus?.job && OVR_ACTIVE.includes(ovrStatus.job.status);
    if (!active) return undefined;
    const t = setInterval(loadOvrStatus, 5000);
    return () => clearInterval(t);
  }, [ovrStatus, loadOvrStatus]);

  useEffect(() => { loadBelkStatus(); }, [loadBelkStatus]);
  useEffect(() => {
    const active = belkStatus?.job && ['queued', 'running'].includes(belkStatus.job.status);
    if (!active) return undefined;
    const t = setInterval(loadBelkStatus, 5000);
    return () => clearInterval(t);
  }, [belkStatus, loadBelkStatus]);

  useEffect(() => { loadQvcStatus(); }, [loadQvcStatus]);
  useEffect(() => {
    const active = qvcStatus?.job && ['queued', 'listing', 'products'].includes(qvcStatus.job.status);
    if (!active) return undefined;
    const t = setInterval(loadQvcStatus, 5000);
    return () => clearInterval(t);
  }, [qvcStatus, loadQvcStatus]);

  // ─── File parse ──────────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setParseError(null);
    setFileName(f.name);
    try {
      const form = new FormData();
      form.append('file', f);
      const token = localStorage.getItem('jwtToken');
      const res = await fetch(API_ENDPOINTS.PRICE_SCRAPER_V2_PARSE_EXCEL, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `HTTP ${res.status}`);
      setSkus(data.skus || []);
      setParsedSizes(data.sizes || {});
      if (!data.skus?.length) setParseError('No SKUs detected. Expected a column named "SKU", "ItemID", or "Item ID".');
    } catch (err) {
      setParseError(err.message);
      setSkus([]);
      setParsedSizes({});
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearImport = () => {
    setSkus([]);
    setParsedSizes({});
    setFileName('');
    setJobName('');
    setParseError(null);
  };

  // ─── Job list polling ────────────────────────────────────────────────────
  const loadJobs = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_LIST_JOBS);
      setJobs(data.jobs || []);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadWatchedJob = useCallback(async () => {
    if (!watchedJobId) { setWatchedJob(null); return; }
    try {
      const data = await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_GET_JOB(watchedJobId));
      setWatchedJob(data.job);

      // Notification when a job transitions from active → completed/failed/cancelled
      const status = data.job?.status;
      const terminal = ['completed', 'failed', 'cancelled'].includes(status);
      if (terminal && !previouslyCompletedIds.current.has(watchedJobId) && notifyEnabled) {
        previouslyCompletedIds.current.add(watchedJobId);
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          const n = new Notification(`Price scraper ${status}`, {
            body: `${data.job.jobName} — ${data.job.completedSkus}/${data.job.totalSkus} SKUs processed`,
            tag: `price-scraper-${watchedJobId}`,
          });
          n.onclick = () => { window.focus(); n.close(); };
        }
      }
    } catch (err) {
      // Non-fatal — user might have navigated to a since-deleted id
      console.warn('watched job fetch failed:', err.message);
    }
  }, [watchedJobId, notifyEnabled]);

  // Poll list every 5 s, watched job every 3 s while active, 20 s otherwise
  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { loadWatchedJob(); }, [loadWatchedJob]);
  useEffect(() => {
    const t = setInterval(loadJobs, 5000);
    return () => clearInterval(t);
  }, [loadJobs]);
  useEffect(() => {
    if (!watchedJobId) return undefined;
    const active = watchedJob && ['preparing', 'queued', 'processing'].includes(watchedJob.status);
    const interval = setInterval(loadWatchedJob, active ? 3000 : 20000);
    return () => clearInterval(interval);
  }, [watchedJobId, watchedJob, loadWatchedJob]);

  // Persist watched id
  useEffect(() => {
    if (watchedJobId) localStorage.setItem(WATCH_KEY, String(watchedJobId));
    else localStorage.removeItem(WATCH_KEY);
  }, [watchedJobId]);

  // ─── Submit ──────────────────────────────────────────────────────────────
  const submitJob = async () => {
    if (!skus.length) return;
    const sites = Object.entries(sitesSelected).filter(([, v]) => v).map(([k]) => k);
    if (!sites.length) { setParseError('Pick at least one site'); return; }
    setSubmitting(true);
    try {
      const data = await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_CREATE_JOB, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skus,
          sites,
          sizes: parsedSizes,
          jobName: jobName || fileName || null,
        }),
      });
      setWatchedJobId(data.jobId);
      clearImport();
      loadJobs();
    } catch (err) {
      setParseError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const cancelJob = async (id) => {
    try {
      await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_CANCEL_JOB(id), { method: 'POST' });
      loadJobs();
      loadWatchedJob();
    } catch (err) { setLoadError(err.message); }
  };

  // ─── Notification permission ─────────────────────────────────────────────
  const toggleNotifications = async () => {
    if (typeof Notification === 'undefined') { setParseError('This browser does not support notifications'); return; }
    if (notifyEnabled) {
      setNotifyEnabled(false);
      localStorage.removeItem(NOTIFY_KEY);
      return;
    }
    const perm = Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();
    if (perm === 'granted') {
      setNotifyEnabled(true);
      localStorage.setItem(NOTIFY_KEY, '1');
    } else {
      setParseError('Notification permission was not granted. You can enable it in the browser settings.');
    }
  };

  // ─── Derived values ─────────────────────────────────────────────────────
  const products = useMemo(() => {
    if (!watchedJob?.products) return [];
    return watchedJob.products;
  }, [watchedJob]);

  // Reset search + page when switching to a different job.
  useEffect(() => {
    setSkuSearch('');
    setResultsPage(1);
  }, [watchedJobId]);

  const filteredProducts = useMemo(() => {
    const q = skuSearch.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => (p.sku || '').toLowerCase().includes(q));
  }, [products, skuSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  // Clamp current page if the filtered set shrank (e.g. user typed a query
  // while sitting on page 30).
  const currentPage = Math.min(resultsPage, totalPages);
  const pagedProducts = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredProducts.slice(start, start + PAGE_SIZE);
  }, [filteredProducts, currentPage]);

  const activeJob = watchedJob && ['preparing', 'queued', 'processing'].includes(watchedJob.status);

  // ─── BBB catalog crawl ─────────────────────────────────────────────────────
  const loadBbbStatus = useCallback(async () => {
    if (!SHOW_BBB_PANEL) return;   // panel hidden — don't poll
    try {
      const d = await authedFetch(API_ENDPOINTS.BBB_CATALOG_STATUS);
      setBbbStatus(d);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadBbbStatus(); }, [loadBbbStatus]);
  useEffect(() => {
    const active = bbbStatus?.job && ['queued', 'listing', 'products'].includes(bbbStatus.job.status);
    const t = setInterval(loadBbbStatus, active ? 4000 : 30000);
    return () => clearInterval(t);
  }, [bbbStatus, loadBbbStatus]);

  const startBbbCrawl = async () => {
    setBbbConfirm(false); setBbbBusy(true); setBbbMsg(null);
    try {
      await authedFetch(API_ENDPOINTS.BBB_CATALOG_START, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: false }),
      });
      setBbbMsg('Catalog crawl started — it runs on the server (~1–2 hours). You can leave this page.');
      loadBbbStatus();
    } catch (e) {
      setBbbMsg(`Could not start: ${e.message}`);
    } finally { setBbbBusy(false); }
  };

  const cancelBbbCrawl = async () => {
    try { await authedFetch(API_ENDPOINTS.BBB_CATALOG_CANCEL, { method: 'POST' }); loadBbbStatus(); }
    catch (e) { setBbbMsg(`Cancel failed: ${e.message}`); }
  };

  const doBbbLookup = async () => {
    const id = bbbLookupId.trim();
    if (!id) return;
    setBbbLookup(null);
    try {
      setBbbLookup(await authedFetch(API_ENDPOINTS.BBB_CATALOG_LOOKUP(id)));
    } catch (e) { setBbbLookup({ error: e.message }); }
  };

  const bbbJob = bbbStatus?.job;
  const bbbActive = bbbJob && ['queued', 'listing', 'products'].includes(bbbJob.status);
  const bbbTotal = bbbStatus?.catalogTotal || 0;
  const bbbDone = bbbStatus?.catalogWithItemId || 0;
  const bbbPending = Math.max(0, bbbTotal - bbbDone);

  // ─── Excel export ────────────────────────────────────────────────────────
  // Builds an .xlsx in the browser from any job's products. Per-store URL
  // columns let the file double as a clickable index of every result.
  const buildExcelFromJob = (job) => {
    const list = job?.products || [];
    if (!list.length) return null;
    const rows = list.map((p) => ({
      SKU: p.sku || '',
      Name: p.name || '',
      Status: p.status || '',
      'Amazon Price': p.amazonPrice || '',
      'Amazon URL': p.amazonUrl || (p.amazonPrice ? (p.url || '') : ''),
      'Amazon ASIN': p.amazonAsin || '',
      'Amazon Resellers #': (p.amazonResellerCount ?? '') === '' ? '' : p.amazonResellerCount,
      'Amazon Resellers (name: price)': p.amazonResellers || '',
      'Walmart Price': p.walmartPrice || '',
      'Walmart URL': p.walmartUrl || '',
      "Lowe's Price": p.lowesPrice || '',
      "Lowe's URL": p.lowesUrl || '',
      'BBB Price': p.bbbPrice || '',
      'BBB URL': p.bbbUrl || '',
      'Home Depot Price': p.homedepotPrice || '',
      'Home Depot URL': p.homedepotUrl || '',
      'Target Price': p.targetPrice || '',
      'Target URL': p.targetUrl || '',
      "Kohl's Price": p.kohlsPrice || '',
      "Kohl's URL": p.kohlsUrl || '',
      'NFM Price': p.nfmPrice || '',
      'NFM URL': p.nfmUrl || '',
      'Ashley Price': p.ashleyPrice || '',
      'Ashley URL': p.ashleyUrl || '',
      'QVC Price': p.qvcPrice || '',
      'QVC URL': p.qvcUrl || '',
      'Chairish Price': p.chairishPrice || '',
      'Chairish URL': p.chairishUrl || '',
      'Antique Farmhouse Price': p.antiquePrice || '',
      'Antique Farmhouse URL': p.antiqueUrl || '',
      'Menards Price': p.menardsPrice || '',
      'Menards URL': p.menardsUrl || '',
      'Belk Price': p.belkPrice || '',
      'Belk URL': p.belkUrl || '',
      'Raymour Price': p.raymourPrice || '',
      'Raymour URL': p.raymourUrl || '',
      'Overstock Price': p.overstockPrice || '',
      'Overstock URL': p.overstockUrl || '',
      'QVC URL': p.qvcUrl || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 16 }, { wch: 60 }, { wch: 14 },
      { wch: 14 }, { wch: 60 }, { wch: 14 }, { wch: 12 }, { wch: 90 },
      { wch: 14 }, { wch: 60 },
      { wch: 14 }, { wch: 60 },
      { wch: 14 }, { wch: 60 },
      { wch: 16 }, { wch: 60 },
      { wch: 16 }, { wch: 60 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Results');
    const safeName = (job.jobName || `Job_${job.id}`).replace(/[^a-z0-9_-]+/gi, '_').slice(0, 60);
    // Filename suffix: _Result_YYYYMMDD_HHmmss in the user's local time so the
    // file stamps when they downloaded it (not when the job started).
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
    XLSX.writeFile(wb, `price_scraper_${safeName}_${job.id}_Result_${stamp}.xlsx`);
    return true;
  };

  const handleExportExcel = () => {
    if (!watchedJob) return;
    buildExcelFromJob(watchedJob);
  };

  // One-click download from the Recent jobs row — fetches the full job (with
  // products) before building the file, since the list endpoint only returns
  // metadata.
  const exportJobExcel = async (jobId) => {
    try {
      const data = await authedFetch(API_ENDPOINTS.PRICE_SCRAPER_V2_GET_JOB(jobId));
      if (!buildExcelFromJob(data.job)) {
        setLoadError('No results to export for this job yet.');
      }
    } catch (err) {
      setLoadError(`Failed to export job: ${err.message}`);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* BBB Catalog card — hidden; BBB resolves via the catalog during scraping */}
      {SHOW_BBB_PANEL && (
      <Paper sx={{ p: 2, mb: 2, borderTop: `3px solid ${ACCENT}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Storefront sx={{ color: ACCENT }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: ACCENT }}>Bed Bath &amp; Beyond Catalog</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Save every Safavieh item BBB carries — <strong>Item ID</strong>, price, URL, and description — so
          you can look up any item's BBB price/link directly by its Item ID. Runs on the server via Scrapfly.
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography variant="body2"><strong>{bbbStatus?.catalogWithItemId ?? 0}</strong> items saved (with Item ID)</Typography>
          <Typography variant="body2" color="text.secondary">{bbbStatus?.catalogTotal ?? 0} product pages discovered</Typography>
        </Box>

        {bbbActive && (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">{bbbJob.currentText || bbbJob.phase || bbbJob.status}</Typography>
              <Typography variant="caption">{Math.round((bbbJob.progress || 0) * 100)}%</Typography>
            </Box>
            <LinearProgress
              variant={bbbJob.phase === 'products' ? 'determinate' : 'indeterminate'}
              value={Math.round((bbbJob.progress || 0) * 100)} />
            <Typography variant="caption" color="text.secondary">
              {bbbJob.savedProducts}/{bbbJob.totalProducts} products · phase: {bbbJob.phase}
            </Typography>
          </Box>
        )}

        {bbbJob && !bbbActive && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Last crawl: {bbbJob.status}{bbbJob.completedAt ? ` · ${new Date(bbbJob.completedAt).toLocaleString()}` : ''}
            {bbbJob.errorLog ? ` · ${String(bbbJob.errorLog).slice(0, 120)}` : ''}
          </Typography>
        )}

        {bbbMsg && <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setBbbMsg(null)}>{bbbMsg}</Alert>}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          {!bbbActive && canBuildCatalog && (
            <Button variant="contained" startIcon={<PlayArrow />} disabled={bbbBusy}
              onClick={() => setBbbConfirm(true)}
              sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}>
              {bbbStatus?.catalogWithItemId ? 'Rebuild / refresh catalog' : 'Build catalog'}
            </Button>
          )}
          {bbbActive && (
            <Button variant="outlined" color="error" startIcon={<Stop />} onClick={cancelBbbCrawl}>
              Cancel crawl
            </Button>
          )}
          {!canBuildCatalog && (
            <Typography variant="caption" color="text.secondary">Building the catalog is limited to admins.</Typography>
          )}
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Look up an item on BBB</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField size="small" placeholder="Item ID (e.g. SG471-8212-9)" value={bbbLookupId}
            onChange={(e) => setBbbLookupId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') doBbbLookup(); }}
            sx={{ minWidth: 260 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          <Button variant="outlined" onClick={doBbbLookup} sx={{ color: ACCENT, borderColor: ACCENT }}>Look up</Button>
        </Box>
        {bbbLookup && (
          <Box sx={{ mt: 1.5 }}>
            {bbbLookup.error && <Alert severity="error">{bbbLookup.error}</Alert>}
            {bbbLookup.success && !bbbLookup.found && (
              <Alert severity="warning">No BBB catalog entry for that Item ID (not crawled yet, or BBB doesn't carry it).</Alert>
            )}
            {bbbLookup.found && bbbLookup.item && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{bbbLookup.item.name}</Typography>
                <Typography variant="body2">
                  Price: <strong>{bbbLookup.item.price != null ? `$${Number(bbbLookup.item.price).toFixed(2)}` : '—'}</strong>
                  {bbbLookup.item.listPrice != null && bbbLookup.item.listPrice !== bbbLookup.item.price
                    ? <>&nbsp;<span style={{ textDecoration: 'line-through', color: '#999' }}>${Number(bbbLookup.item.listPrice).toFixed(2)}</span></>
                    : null}
                </Typography>
                {bbbLookup.item.url && (
                  <Link href={bbbLookup.item.url} target="_blank" rel="noopener" variant="body2">Open on Bed Bath &amp; Beyond ↗</Link>
                )}
                {bbbLookup.item.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {String(bbbLookup.item.description).slice(0, 300)}{String(bbbLookup.item.description).length > 300 ? '…' : ''}
                  </Typography>
                )}
                {Array.isArray(bbbLookup.item.options) && bbbLookup.item.options.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                      {bbbLookup.item.optionCount || bbbLookup.item.options.length} sizes/colors on BBB:
                    </Typography>
                    <Box sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      {bbbLookup.item.options.map((o, idx) => (
                        <Box key={o.id || idx}
                          sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, px: 1, py: 0.25,
                                borderBottom: idx < bbbLookup.item.options.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider', fontSize: '0.78rem' }}>
                          <span>{o.desc || o.subSku || o.id}{o.inStock === false ? ' · out of stock' : ''}</span>
                          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {o.price != null ? `$${Number(o.price).toFixed(2)}`
                              : (o.base != null ? `$${Number(o.base).toFixed(2)}` : '—')}
                          </span>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                {bbbLookup.item.updatedAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Updated: {new Date(bbbLookup.item.updatedAt).toLocaleString()}
                  </Typography>
                )}
              </Paper>
            )}
          </Box>
        )}
      </Paper>
      )}

      {/* BBB build confirmation */}
      {SHOW_BBB_PANEL && (
      <Dialog open={bbbConfirmOpen} onClose={() => setBbbConfirm(false)}>
        <DialogTitle>Build the BBB catalog?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            BBB carries <strong>{bbbTotal ? bbbTotal.toLocaleString() : '~22,900'} Safavieh products</strong> (all
            categories, enumerated from BBB's sitemap). This fetches each product's Item ID, price, URL, and
            description into the database.
            <ul style={{ margin: '10px 0', paddingLeft: 18 }}>
              <li>Already saved: <strong>{bbbDone.toLocaleString()}</strong> · still to fetch: <strong>{bbbPending ? bbbPending.toLocaleString() : '~17,000'}</strong> (it skips ones already done).</li>
              <li>Runs on the server (~2–4 hours) — you can close the page.</li>
              <li>Uses Scrapfly: roughly <strong>~{Math.max(1, Math.round((bbbPending || 17000) * 25 / 1000)).toLocaleString()}k credits (~${Math.round((bbbPending || 17000) * 25 * 0.00035)})</strong>, well within the monthly plan.</li>
              <li>Re-running later refreshes prices for items already saved.</li>
            </ul>
            Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBbbConfirm(false)}>Cancel</Button>
          <Button variant="contained" onClick={startBbbCrawl}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            Yes, build it
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Beyond partner dashboard — the BBB / Overstock partner export maps every
          ItemID to the site's Product ID + Product Option ID, the same ids the
          live page carries. The worker opens that page and prices the exact
          option; the old size/colour guess is only the fallback for items the
          file does not have. Prices in the file are a snapshot; scraping reads
          them live. */}
      {canBuildCatalog && (
      <Paper
        elevation={0}
        sx={{
          p: 2.5, mb: 2, borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? 'background.paper' : ACCENT_LIGHT,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Bed Bath &amp; Beyond + Overstock item map</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {bpStatus?.sites?.length
            ? bpStatus.sites.map(x => `${x.site === 'bbb' ? 'BBB' : 'Overstock'}: ${x.items.toLocaleString()} items (${x.rows.toLocaleString()} options)`).join(' · ')
              + (bpStatus.job?.completedAt ? ` · loaded ${new Date(bpStatus.job.completedAt).toLocaleString()}${bpStatus.job.user ? ` by ${bpStatus.job.user}` : ''}` : '')
            : 'Not loaded yet — upload the Beyond partner dashboard export (Product Dashboard, sheets OSK + BBB) so BBB and Overstock scrapes price the exact option.'}
        </Typography>

        {bpStatus?.job && BP_ACTIVE.includes(bpStatus.job.status) ? (
          <Box sx={{ mb: 1 }}>
            <LinearProgress sx={{ height: 8, borderRadius: 1, mb: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
              {bpStatus.job.currentText || bpStatus.job.status}
            </Typography>
          </Box>
        ) : null}

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            component="label"
            disabled={bpBusy || (bpStatus?.job && BP_ACTIVE.includes(bpStatus.job.status))}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}
          >
            {bpBusy ? 'Uploading…' : 'Upload dashboard export (.xlsx)'}
            <input ref={bpFileRef} type="file" accept=".xlsx" hidden onChange={uploadBpFile} />
          </Button>
          <Button variant="text" startIcon={<Refresh />} onClick={loadBpStatus}>Refresh status</Button>
        </Stack>

        {bpMsg && (
          <Alert severity={bpMsg.severity} sx={{ mt: 1.5 }} onClose={() => setBpMsg(null)}>
            {bpMsg.text}
          </Alert>
        )}
        {bpStatus?.job?.status === 'completed' && bpStatus.job.currentText && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{bpStatus.job.currentText}</Typography>
        )}
        {bpStatus?.job?.status === 'failed' && bpStatus.job.errorLog && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{bpStatus.job.errorLog}</Alert>
        )}
      </Paper>
      )}

      {/* Raymour catalog panel — their product URLs end with the RAY4484 item
          code, so one sweep of their sitemaps gives the whole code -> URL map.
          Their search only redirects to the product in a browser (client-side),
          which is why the mapping has to be stored rather than searched for.
          Prices are read live at scrape time, so this never goes stale. */}
      {SHOW_CATALOG_PANELS && canBuildCatalog && (
      <Paper
        elevation={0}
        sx={{
          p: 2.5, mb: 2, borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? 'background.paper' : ACCENT_LIGHT,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Raymour &amp; Flanigan Catalog</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {rayStatus?.catalogCount
            ? `${rayStatus.catalogCount.toLocaleString()} item codes mapped to product pages. Prices are fetched live when you scrape.`
            : 'Not built yet — Raymour needs this map to know which page sells each item code.'}
        </Typography>

        {rayStatus?.job && ['queued', 'running'].includes(rayStatus.job.status) ? (
          <Box sx={{ mb: 1 }}>
            <LinearProgress
              variant="determinate"
              value={Math.round((rayStatus.job.progress || 0) * 100)}
              sx={{ height: 8, borderRadius: 1, mb: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary">
              {rayStatus.job.currentText || rayStatus.job.phase || rayStatus.job.status}
            </Typography>
          </Box>
        ) : null}

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            disabled={rayBusy || (rayStatus?.job && ['queued', 'running'].includes(rayStatus.job.status))}
            onClick={startRayBuild}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}
          >
            Build / Refresh Catalog
          </Button>
          <Button variant="text" startIcon={<Refresh />} onClick={loadRayStatus}>Refresh status</Button>
        </Stack>

        {rayMsg && (
          <Alert severity={rayMsg.severity} sx={{ mt: 1.5 }} onClose={() => setRayMsg(null)}>
            {rayMsg.text}
          </Alert>
        )}
        {rayStatus?.job?.status === 'failed' && rayStatus.job.errorLog && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{rayStatus.job.errorLog}</Alert>
        )}
      </Paper>
      )}

      {/* Overstock catalog panel — overstock.com is its own storefront again
          (Beyond Inc relaunch), with its own assortment and prices, and its
          search is not reachable by URL. Like BBB, one product page carries one
          Model Number (our ItemID) plus a size/color variation list, so the
          catalog maps every Safavieh page and a scrape matches the item's
          size/color/shape to a variation and reads that price live. */}
      {SHOW_CATALOG_PANELS && canBuildCatalog && (
      <Paper
        elevation={0}
        sx={{
          p: 2.5, mb: 2, borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? 'background.paper' : ACCENT_LIGHT,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Overstock Catalog</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {ovrStatus?.catalogTotal
            ? `${ovrStatus.catalogTotal.toLocaleString()} Safavieh product pages mapped (${(ovrStatus.catalogWithItemId || 0).toLocaleString()} with an ItemID). Prices are read live when you scrape.`
            : 'Not built yet — Overstock needs this map to know which page sells each item.'}
        </Typography>

        {ovrStatus?.job && OVR_ACTIVE.includes(ovrStatus.job.status) ? (
          <Box sx={{ mb: 1 }}>
            <LinearProgress
              variant="determinate"
              value={Math.round((ovrStatus.job.progress || 0) * 100)}
              sx={{ height: 8, borderRadius: 1, mb: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary">
              {ovrStatus.job.currentText || ovrStatus.job.phase || ovrStatus.job.status}
            </Typography>
          </Box>
        ) : null}

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            disabled={ovrBusy || (ovrStatus?.job && OVR_ACTIVE.includes(ovrStatus.job.status))}
            onClick={startOvrBuild}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}
          >
            Build / Refresh Catalog
          </Button>
          {ovrStatus?.job && OVR_ACTIVE.includes(ovrStatus.job.status) && (
            <Button variant="outlined" color="warning" onClick={cancelOvrBuild}>Cancel</Button>
          )}
          <Button variant="text" startIcon={<Refresh />} onClick={loadOvrStatus}>Refresh status</Button>
        </Stack>

        {ovrMsg && (
          <Alert severity={ovrMsg.severity} sx={{ mt: 1.5 }} onClose={() => setOvrMsg(null)}>
            {ovrMsg.text}
          </Alert>
        )}
        {ovrStatus?.job?.status === 'failed' && ovrStatus.job.errorLog && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{ovrStatus.job.errorLog}</Alert>
        )}
      </Paper>
      )}

      {/* Belk catalog panel — Belk searches fine on a 13-digit UPC (our feeds
          store 12; the leading zero is the difference between one result and
          none), but each lookup needs residential+ASP+render_js. Since one
          collection page prices 50-85 of our UPCs at once, the worker harvests
          whole pages into belk_catalog and scrape-time pricing is a DB read. */}
      {SHOW_CATALOG_PANELS && canBuildCatalog && (
      <Paper
        elevation={0}
        sx={{
          p: 2.5, mb: 2, borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? 'background.paper' : ACCENT_LIGHT,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Belk Catalog</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {belkStatus?.catalogCount
            ? `${belkStatus.catalogCount.toLocaleString()} UPCs priced from ${(belkStatus.productCount || 0).toLocaleString()} product pages.`
            : 'Not built yet — Belk prices come from this catalog.'}
        </Typography>

        {belkStatus?.job && ['queued', 'running'].includes(belkStatus.job.status) ? (
          <Box sx={{ mb: 1 }}>
            <LinearProgress
              variant={belkStatus.job.phase === 'resolving' ? 'determinate' : 'indeterminate'}
              value={Math.round((belkStatus.job.progress || 0) * 100)}
              sx={{ height: 8, borderRadius: 1, mb: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary">
              {belkStatus.job.currentText || belkStatus.job.phase || belkStatus.job.status}
            </Typography>
          </Box>
        ) : null}

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            disabled={belkBusy || (belkStatus?.job && ['queued', 'running'].includes(belkStatus.job.status))}
            onClick={() => setBelkConfirm(true)}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}
          >
            Build / Update Catalog
          </Button>
          {belkStatus?.job && ['queued', 'running'].includes(belkStatus.job.status) && (
            <Button variant="outlined" color="error" startIcon={<Stop />} onClick={cancelBelkBuild}>
              Cancel
            </Button>
          )}
          <Button variant="text" startIcon={<Refresh />} onClick={loadBelkStatus}>Refresh status</Button>
        </Stack>

        {belkMsg && (
          <Alert severity={belkMsg.severity} sx={{ mt: 1.5 }} onClose={() => setBelkMsg(null)}>
            {belkMsg.text}
          </Alert>
        )}
        {belkStatus?.job?.status === 'failed' && belkStatus.job.errorLog && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{belkStatus.job.errorLog}</Alert>
        )}
      </Paper>
      )}

      {/* Belk build confirmation */}
      <Dialog open={belkConfirmOpen} onClose={() => setBelkConfirm(false)}>
        <DialogTitle>Build the Belk catalog?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This resolves every Belk UPC we don't have yet and stores its price.
            <ul style={{ marginTop: 8, marginBottom: 8 }}>
              <li>One product page prices 50-85 items at once, so it needs far fewer fetches than items.</li>
              <li>Runs on the EC2 worker — you can close this page.</li>
              <li>Belk needs the residential + ASP + rendered recipe, so it is not free; re-runs only fetch what's still missing.</li>
              <li>Existing rows are updated in place, so re-running is safe.</li>
            </ul>
            Start now?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBelkConfirm(false)}>Cancel</Button>
          <Button variant="contained" onClick={startBelkBuild} disabled={belkBusy}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            {belkBusy ? 'Starting…' : 'Start build'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QVC catalog panel — QVC can't be searched by our CustomerItemCode and
          the product URL can't be derived from it, so we crawl the Safavieh
          brand listing once and store code -> URL. Prices are still fetched
          live at scrape time. Admin/owner only, like the BBB crawl was.

          HIDDEN 2026-08-19: the catalog is built out to 97.9% of the QVC1741
          feed (8,949 of 9,141 codes) and the 192 stragglers are delisted
          products, so there is nothing left for a re-crawl to find. The
          endpoints and worker are untouched — flip SHOW_QVC_CATALOG back to
          true if QVC ever adds a batch of new items. */}
      {SHOW_QVC_CATALOG && canBuildCatalog && (
      <Paper
        elevation={0}
        sx={{
          p: 2.5, mb: 2, borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? 'background.paper' : ACCENT_LIGHT,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Storefront sx={{ color: ACCENT }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>QVC Catalog</Typography>
          {qvcStatus && (
            <Chip
              size="small"
              label={`${(qvcStatus.catalogTotal || 0).toLocaleString()} codes · ${(qvcStatus.catalogProducts || 0).toLocaleString()} products`}
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          QVC's search doesn't work on our item codes and the product URL can't be derived from
          them, so we crawl QVC's Safavieh listing (<strong>~2,800 products</strong>) once and save
          every offer code with its product URL. Each colour variant is saved as its own row — sizes
          are separate QVC products, so they get their own URLs. Prices are still fetched
          <strong> live</strong> when you run a scrape; this catalog only supplies the link.
        </Typography>

        {qvcStatus?.job && ['queued', 'listing', 'products'].includes(qvcStatus.job.status) ? (
          <Box sx={{ mb: 1 }}>
            <LinearProgress
              variant={qvcStatus.job.phase === 'products' ? 'determinate' : 'indeterminate'}
              value={Math.round((qvcStatus.job.progress || 0) * 100)}
              sx={{ height: 8, borderRadius: 1, mb: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary">
              {qvcStatus.job.currentText || qvcStatus.job.phase || qvcStatus.job.status}
              {qvcStatus.job.totalProducts
                ? ` · ${qvcStatus.job.savedProducts}/${qvcStatus.job.totalProducts} products`
                : ''}
              {qvcStatus.job.savedCodes ? ` · ${qvcStatus.job.savedCodes} codes` : ''}
            </Typography>
          </Box>
        ) : null}

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            disabled={qvcBusy || (qvcStatus?.job && ['queued', 'listing', 'products'].includes(qvcStatus.job.status))}
            onClick={() => setQvcConfirm(true)}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}
          >
            Build / Refresh Catalog
          </Button>
          {/* The brand listing only carries ~2,800 rug-heavy products out of
              9,113 feed codes, so a full crawl still leaves roughly half the
              feed uncovered. This fetches those missing codes directly. */}
          <Button
            variant="outlined"
            startIcon={<PlayArrow />}
            disabled={qvcBusy || (qvcStatus?.job && ['queued', 'listing', 'products'].includes(qvcStatus.job.status))}
            onClick={() => startQvcCrawl('gapfill')}
            sx={{ borderColor: ACCENT, color: ACCENT }}
          >
            Fill Gaps from Feed
          </Button>
          {qvcStatus?.job && ['queued', 'listing', 'products'].includes(qvcStatus.job.status) && (
            <Button variant="outlined" color="error" startIcon={<Stop />} onClick={cancelQvcCrawl}>
              Cancel
            </Button>
          )}
          <Button variant="text" startIcon={<Refresh />} onClick={loadQvcStatus}>Refresh status</Button>
        </Stack>

        {qvcMsg && (
          <Alert severity={qvcMsg.severity} sx={{ mt: 1.5 }} onClose={() => setQvcMsg(null)}>
            {qvcMsg.text}
          </Alert>
        )}
        {qvcStatus?.job?.status === 'failed' && qvcStatus.job.errorLog && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{qvcStatus.job.errorLog}</Alert>
        )}
      </Paper>
      )}

      {/* QVC crawl confirmation */}
      <Dialog open={qvcConfirmOpen} onClose={() => setQvcConfirm(false)}>
        <DialogTitle>Build the QVC catalog?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This crawls QVC's entire Safavieh listing — about <strong>2,800 products</strong> — and
            saves every offer code with its product URL (all colours and all sizes).
            <ul style={{ marginTop: 8, marginBottom: 8 }}>
              <li>Runs on the EC2 worker — you can close this page.</li>
              <li>Takes a while: QVC is slow and rate-limits, so pages are fetched politely with retries.</li>
              <li>Costs roughly <strong>$2</strong> in Scrapfly credits.</li>
              <li>Existing rows are updated in place, so re-running is safe.</li>
            </ul>
            Start now?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQvcConfirm(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => startQvcCrawl('listing')} disabled={qvcBusy}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            {qvcBusy ? 'Starting…' : 'Start crawl'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit card */}
      <Paper sx={{ p: 2, mb: 2, borderTop: `3px solid ${ACCENT}` }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: ACCENT }}>Submit a new job</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload an Excel/CSV file with a <strong>SKU</strong> (or <strong>ItemID</strong>) column,
          pick the sites, hit Start. The scrape runs on the server — you can close this tab and come
          back later; the job keeps running.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            component="label"
            disabled={submitting}
            sx={{ color: ACCENT, borderColor: ACCENT, '&:hover': { borderColor: ACCENT_HOVER, bgcolor: ACCENT_LIGHT } }}
          >
            Upload Excel / CSV
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
            />
          </Button>
          {fileName && (
            <Chip
              label={`${fileName} (${skus.length} SKUs)`}
              onDelete={clearImport}
              deleteIcon={<Clear />}
              variant="outlined"
              sx={skus.length ? { color: ACCENT, borderColor: ACCENT } : {}}
            />
          )}
          <TextField
            size="small"
            label="Job name (optional)"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            sx={{ minWidth: 240 }}
            disabled={submitting}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="body2" sx={{ mr: 1, fontWeight: 600 }}>Sites:</Typography>
          {SITES.map(s => (
            <FormControlLabel
              key={s.key}
              control={<Checkbox
                checked={!!sitesSelected[s.key]}
                onChange={(e) => setSitesSelected(v => ({ ...v, [s.key]: e.target.checked }))}
                disabled={submitting}
                sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
              />}
              label={s.label}
            />
          ))}
        </Box>

        {parseError && <Alert severity="error" onClose={() => setParseError(null)} sx={{ mb: 2 }}>{parseError}</Alert>}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
            disabled={!skus.length || submitting || !Object.values(sitesSelected).some(Boolean)}
            onClick={submitJob}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}
          >
            {submitting ? 'Submitting…' : `Start Scraping ${skus.length} SKU${skus.length === 1 ? '' : 's'}`}
          </Button>
          <Button
            size="small"
            onClick={toggleNotifications}
            startIcon={notifyEnabled ? <Notifications /> : <NotificationsOff />}
            sx={notifyEnabled ? { color: ACCENT } : {}}
          >
            {notifyEnabled ? 'Notifications on' : 'Notify on completion'}
          </Button>
        </Box>
      </Paper>

      {/* Watched job detail */}
      {watchedJob && (
        <Paper sx={{ p: 2, mb: 2, borderTop: `3px solid ${ACCENT}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: ACCENT }}>
                {watchedJob.jobName || `Job #${watchedJob.id}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                #{watchedJob.id} · {watchedJob.createdBy || '—'} · {fmtDuration(watchedJob.startedAt, watchedJob.completedAt)}
                {' · '}
                {watchedJob.completedSkus}/{watchedJob.totalSkus} SKUs
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                label={watchedJob.status}
                color={statusChipColor(watchedJob.status)}
                variant={watchedJob.status === 'processing' ? 'filled' : 'outlined'}
              />
              {activeJob && (
                <Tooltip title="Stop this job">
                  <IconButton size="small" color="error" onClick={() => cancelJob(watchedJob.id)}>
                    <Stop />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Close this view">
                <IconButton size="small" onClick={() => setWatchedJobId(null)}>
                  <Clear />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <LinearProgress
            variant="determinate"
            value={Math.min(100, (watchedJob.progress || 0) * 100)}
            sx={{ mb: 1, height: 8, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }}
          />
          {watchedJob.currentText && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {watchedJob.currentText}
            </Typography>
          )}

          {watchedJob.status === 'failed' && watchedJob.errorLog && (
            <Alert severity="error" sx={{ my: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {watchedJob.errorLog}
              </Typography>
            </Alert>
          )}

          {/* Results toolbar: search + export */}
          {products.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search SKU…"
                value={skuSearch}
                onChange={(e) => { setSkuSearch(e.target.value); setResultsPage(1); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: skuSearch && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSkuSearch('')}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 240 }}
              />
              <Typography variant="caption" color="text.secondary">
                {skuSearch
                  ? `${filteredProducts.length} match${filteredProducts.length === 1 ? '' : 'es'} of ${products.length}`
                  : `${products.length} SKU${products.length === 1 ? '' : 's'}`}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                variant="outlined"
                startIcon={<GetApp />}
                onClick={handleExportExcel}
                sx={{ color: ACCENT, borderColor: ACCENT, '&:hover': { borderColor: ACCENT_HOVER, bgcolor: ACCENT_LIGHT } }}
              >
                Download Excel
              </Button>
            </Box>
          )}

          {/* Results table */}
          {products.length > 0 && (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: 500,
                // Dark-mode scrollbar — default Webkit chrome ships a white
                // scrollbar background that flashes against the dark Paper.
                '&::-webkit-scrollbar': { width: 10, height: 10 },
                '&::-webkit-scrollbar-track': {
                  bgcolor: isDark ? '#1e1e1e' : '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: isDark ? '#555' : '#bbb',
                  borderRadius: 5,
                  '&:hover': { bgcolor: isDark ? '#777' : '#999' },
                },
                '&::-webkit-scrollbar-corner': {
                  bgcolor: isDark ? '#1e1e1e' : '#f1f1f1',
                },
                scrollbarColor: isDark ? '#555 #1e1e1e' : '#bbb #f1f1f1',
                scrollbarWidth: 'thin',
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>SKU</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Amazon</TableCell>
                    <TableCell>Walmart</TableCell>
                    <TableCell>Lowe's</TableCell>
                    <TableCell>BBB</TableCell>
                    <TableCell>Home Depot</TableCell>
                    <TableCell>Target</TableCell>
                    <TableCell>Kohl's</TableCell>
                    <TableCell>NFM</TableCell>
                    <TableCell>Ashley</TableCell>
                    <TableCell>QVC</TableCell>
                    <TableCell>Chairish</TableCell>
                    <TableCell>Antique FH</TableCell>
                    <TableCell>Menards</TableCell>
                    <TableCell>Belk</TableCell>
                    <TableCell>Raymour</TableCell>
                    <TableCell>Overstock</TableCell>
                    <TableCell>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={19} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No SKUs match "{skuSearch}".
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {pagedProducts.map((p, i) => (
                    <TableRow key={`${p.sku}-${(currentPage - 1) * PAGE_SIZE + i}`} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{p.sku}</TableCell>
                      <TableCell>
                        <Chip size="small" label={p.status || '—'} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {p.amazonPrice
                          ? (p.amazonUrl || p.url
                              ? <Link href={p.amazonUrl || p.url} target="_blank" rel="noreferrer">{p.amazonPrice}</Link>
                              : p.amazonPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.walmartPrice
                          ? (p.walmartUrl
                              ? <Link href={p.walmartUrl} target="_blank" rel="noreferrer">{p.walmartPrice}</Link>
                              : p.walmartPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.lowesPrice
                          ? (p.lowesUrl
                              ? <Link href={p.lowesUrl} target="_blank" rel="noreferrer">{p.lowesPrice}</Link>
                              : p.lowesPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.bbbPrice
                          ? (p.bbbUrl
                              ? <Link href={p.bbbUrl} target="_blank" rel="noreferrer">{p.bbbPrice}</Link>
                              : p.bbbPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.homedepotPrice
                          ? (p.homedepotUrl
                              ? <Link href={p.homedepotUrl} target="_blank" rel="noreferrer">{p.homedepotPrice}</Link>
                              : p.homedepotPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.targetPrice
                          ? (p.targetUrl
                              ? <Link href={p.targetUrl} target="_blank" rel="noreferrer">{p.targetPrice}</Link>
                              : p.targetPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.kohlsPrice
                          ? (p.kohlsUrl
                              ? <Link href={p.kohlsUrl} target="_blank" rel="noreferrer">{p.kohlsPrice}</Link>
                              : p.kohlsPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.nfmPrice
                          ? (p.nfmUrl
                              ? <Link href={p.nfmUrl} target="_blank" rel="noreferrer">{p.nfmPrice}</Link>
                              : p.nfmPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.ashleyPrice
                          ? (p.ashleyUrl
                              ? <Link href={p.ashleyUrl} target="_blank" rel="noreferrer">{p.ashleyPrice}</Link>
                              : p.ashleyPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.qvcPrice
                          ? (p.qvcUrl
                              ? <Link href={p.qvcUrl} target="_blank" rel="noreferrer">{p.qvcPrice}</Link>
                              : p.qvcPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.chairishPrice
                          ? (p.chairishUrl
                              ? <Link href={p.chairishUrl} target="_blank" rel="noreferrer">{p.chairishPrice}</Link>
                              : p.chairishPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.antiquePrice
                          ? (p.antiqueUrl
                              ? <Link href={p.antiqueUrl} target="_blank" rel="noreferrer">{p.antiquePrice}</Link>
                              : p.antiquePrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.menardsPrice
                          ? (p.menardsUrl
                              ? <Link href={p.menardsUrl} target="_blank" rel="noreferrer">{p.menardsPrice}</Link>
                              : p.menardsPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.belkPrice
                          ? (p.belkUrl
                              ? <Link href={p.belkUrl} target="_blank" rel="noreferrer">{p.belkPrice}</Link>
                              : p.belkPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.raymourPrice
                          ? (p.raymourUrl
                              ? <Link href={p.raymourUrl} target="_blank" rel="noreferrer">{p.raymourPrice}</Link>
                              : p.raymourPrice)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.overstockPrice
                          ? (p.overstockUrl
                              ? <Link href={p.overstockUrl} target="_blank" rel="noreferrer">{p.overstockPrice}</Link>
                              : p.overstockPrice)
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={p.name || ''}>
                          <span>{p.name || '—'}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Results pagination footer */}
          {filteredProducts.length > PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, p) => setResultsPage(p)}
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Paper>
      )}

      {/* Jobs list */}
      <Paper sx={{ p: 2, borderTop: `3px solid ${ACCENT}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: ACCENT }}>Recent jobs</Typography>
          <IconButton size="small" onClick={loadJobs} disabled={loadingList}>
            {loadingList ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
          </IconButton>
        </Box>
        {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Elapsed</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((j) => {
                const pct = Math.round((j.progress || 0) * 100);
                const isWatching = j.id === watchedJobId;
                return (
                  <TableRow key={j.id} hover sx={{ bgcolor: isWatching ? (isDark ? 'rgba(255,140,0,0.18)' : ACCENT_LIGHT) : 'inherit' }}>
                    <TableCell>{j.jobName || `Job #${j.id}`}</TableCell>
                    <TableCell>{j.createdBy || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={j.status} color={statusChipColor(j.status)} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
                        <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 6, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }} />
                        <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'right' }}>
                          {j.completedSkus}/{j.totalSkus}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{j.startedAt ? new Date(j.startedAt).toLocaleString() : '—'}</TableCell>
                    <TableCell>{fmtDuration(j.startedAt, j.completedAt)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => setWatchedJobId(j.id)} sx={{ color: ACCENT }}>View</Button>
                      {['queued', 'processing'].includes(j.status) && (
                        <Button size="small" color="error" onClick={() => cancelJob(j.id)}>Stop</Button>
                      )}
                      {['completed', 'failed', 'cancelled'].includes(j.status) && (
                        <Tooltip title="Download Excel">
                          <IconButton size="small" onClick={() => exportJobExcel(j.id)} sx={{ color: ACCENT }}>
                            <GetApp fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {jobs.length === 0 && !loadingList && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No jobs yet. Upload an Excel above to start your first one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
